import type {
    ChangeEvent, ChangeEventHandler, ClipboardEvent, ClipboardEventHandler,
    FocusEvent, FocusEventHandler, HTMLInputTypeAttribute, KeyboardEvent, KeyboardEventHandler,
} from 'react';

export type IntegerTextBoxType = 'integer' | 'positive-integer';
export type DecimalPlaceCount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
export type DecimalTextBoxType = 'decimal' | `decimal-${DecimalPlaceCount}`;
export type NumericTextBoxType = IntegerTextBoxType | DecimalTextBoxType;

const DECIMAL_TYPE_PATTERN = /^decimal(-(10|[1-9]))?$/;

const isDecimalType = (type?: string): type is DecimalTextBoxType =>
    typeof type === 'string' && DECIMAL_TYPE_PATTERN.test(type);

export const isNumericType = (type?: string): type is NumericTextBoxType =>
    type === 'integer' || type === 'positive-integer' || isDecimalType(type);

// 'decimal' alone carries no digit suffix and allows an unlimited number of decimal places; null
// signals that (as opposed to 0, which would mean "no decimal places allowed").
const getMaxDecimalPlaces = (type: DecimalTextBoxType): number | null =>
    type === 'decimal' ? null : Number(type.slice('decimal-'.length));

// Blocked outright for every numeric type: exponent notation and an explicit leading "+" are never
// meaningful in these fields. Integer types additionally block "." (no fractional part); decimal
// types allow exactly one "." instead, handled separately below since it needs an "already have
// one" check rather than an unconditional block.
const INTEGER_BLOCKED_KEYS = ['+', 'e', 'E', '.'];
const DECIMAL_BLOCKED_KEYS = ['+', 'e', 'E'];

// The DOM's own max/min validation (rangeOverflow/rangeUnderflow) only flags an out-of-range value
// after the fact (e.g. on blur/submit) — it doesn't stop the keystroke that caused it. To actually
// enforce a max while typing, we read the input's own `max` attribute off event.target and reject
// the keystroke/paste that would push the value past it, before the browser commits it.
// event.target (not currentTarget) is used deliberately: MUI's InputBase only forwards onKeyDown
// to the native <input> — onPaste isn't in its recognized prop list, so it's spread onto the
// outer wrapper <div> instead, making currentTarget unreliable for paste. target is always the
// actual <input> that was typed into or pasted onto.
//
// Cursor position can't be checked: selectionStart/selectionEnd are unsupported on <input type="number">
// (and we deliberately don't rely on them for decimal types either, to keep one code path for both).
// So the prospective value is built by appending to the current value, not inserting at the caret
// (same limitation the '-' and '.' duplicate checks below already have).
const exceedsMax = (currentValue: string, incomingText: string, max: string) => {
    if (max === '') {
        return false;
    }
    const prospectiveValue = Number(`${currentValue}${incomingText}`);
    return !Number.isNaN(prospectiveValue) && prospectiveValue > Number(max);
};

// `min` is NOT enforced on keydown the way `max` is. Appending a digit to a non-negative number
// only ever grows it, so blocking a digit that currently reads below `max` is always safe — you're
// only ever approaching or exceeding `max`, never retreating from it. `min` is the opposite: values
// start small and grow as you type, so blocking every digit that's currently below `min` would also
// block legitimate in-progress typing toward a multi-digit min (e.g. min=10: the first keystroke "1"
// is < 10 and would be wrongly rejected, even though it's a valid step toward "10"). So `min` is
// enforced at two points instead: on paste (see makeNumericPasteHandler), where the pasted text is
// already a complete final value with no progressive-typing ambiguity; and on blur (see
// makeNumericBlurHandler), which clamps a sub-min value up to `min` once the user is done editing.
// This is also how a positive-only decimal field rejects a leading "-": there's no keydown-level
// block for it (every decimal type structurally allows one leading "-", same as plain 'integer'),
// so a caller-provided `min` of 0 catches it here and on blur instead.
const belowMin = (currentValue: string, incomingText: string, min: string) => {
    if (min === '') {
        return false;
    }
    const prospectiveValue = Number(`${currentValue}${incomingText}`);
    return !Number.isNaN(prospectiveValue) && prospectiveValue < Number(min);
};

const makeNumericKeyDownHandler = (type: NumericTextBoxType | undefined, onKeyDown?: KeyboardEventHandler<HTMLInputElement>) =>
    (event: KeyboardEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement;
        const isDecimal = isDecimalType(type);

        if (type) {
            const blockedKeys = [...(isDecimal ? DECIMAL_BLOCKED_KEYS : INTEGER_BLOCKED_KEYS)];
            if (type === 'positive-integer' || target.value.includes('-')) {
                blockedKeys.push('-');
            }
            if (blockedKeys.includes(event.key)) {
                event.preventDefault();
                onKeyDown?.(event);
                return;
            }
            if (isDecimal && event.key === '.' && target.value.includes('.')) {
                event.preventDefault();
                onKeyDown?.(event);
                return;
            }
        }

        if (/^\d$/.test(event.key)) {
            const maxDecimalPlaces = isDecimal ? getMaxDecimalPlaces(type as DecimalTextBoxType) : null;
            const decimalPart = target.value.split('.')[1];
            const exceedsDecimalPlaces = maxDecimalPlaces != null && decimalPart != null && decimalPart.length >= maxDecimalPlaces;

            // Only single digit keys grow the value or its decimal portion; Backspace/Delete/arrows/etc. never need either check.
            if (exceedsDecimalPlaces || exceedsMax(target.value, event.key, target.max)) {
                event.preventDefault();
            }
        }
        onKeyDown?.(event);
    };

const getPastePattern = (type: NumericTextBoxType): RegExp => {
    if (type === 'positive-integer') return /^\d+$/;
    if (type === 'integer') return /^-?\d+$/;
    const maxDecimalPlaces = getMaxDecimalPlaces(type);
    const decimalDigits = maxDecimalPlaces == null ? '\\d+' : `\\d{1,${maxDecimalPlaces}}`;
    return new RegExp(`^-?\\d+(\\.${decimalDigits})?$`);
};

const makeNumericPasteHandler = (type: NumericTextBoxType | undefined, onPaste?: ClipboardEventHandler<HTMLInputElement>) =>
    (event: ClipboardEvent<HTMLInputElement>) => {
        const pastedText = event.clipboardData.getData('text');
        if (type) {
            if (!getPastePattern(type).test(pastedText)) {
                event.preventDefault();
                onPaste?.(event);
                return;
            }
        }
        const target = event.target as HTMLInputElement;
        if (exceedsMax(target.value, pastedText, target.max) || belowMin(target.value, pastedText, target.min)) {
            event.preventDefault();
        }
        onPaste?.(event);
    };

// On blur, clamp a value that ended up below `min` back up to `min`. Building a plain object for
// the synthetic target (rather than spreading event.target) is deliberate: event.target is a real
// HTMLInputElement, and spreading a DOM node copies nothing (its properties live on the prototype),
// so only the fields consumers actually read (name/id/type/value/checked) are picked off explicitly.
// This relies on TextBox being used as a controlled input — calling onChange updates the caller's
// state, and React re-renders the native input with the corrected value; there's no direct DOM write.
const makeNumericBlurHandler = (onChange?: ChangeEventHandler<HTMLInputElement>, onBlur?: FocusEventHandler<HTMLInputElement>) =>
    (event: FocusEvent<HTMLInputElement>) => {
        const target = event.target;
        if (target.min !== '' && target.value !== '' && Number(target.value) < Number(target.min)) {
            onChange?.({
                ...event,
                target: { name: target.name, id: target.id, type: target.type, value: target.min, checked: target.checked },
            } as unknown as ChangeEvent<HTMLInputElement>);
        }
        onBlur?.(event);
    };

const getAutoSlotProps = (type: string | undefined, slotProps: any) => {
    if (type === 'positive-integer') {
        return { ...slotProps, input: { min: 0, ...slotProps?.input } };
    }
    if (isDecimalType(type)) {
        return { ...slotProps, input: { inputMode: 'decimal', ...slotProps?.input } };
    }
    return slotProps;
};

export interface UseNumericTextBoxBehaviorArgs {
    type?: NumericTextBoxType | HTMLInputTypeAttribute;
    slotProps?: any;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    onPaste?: ClipboardEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
}

export interface UseNumericTextBoxBehaviorResult {
    htmlType?: HTMLInputTypeAttribute;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    onPaste?: ClipboardEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    slotProps?: any;
}

// Not a stateful hook — this is a pure props-in/props-out derivation, nothing here holds its own
// state — but it's named and shaped like one (a `use*` function called unconditionally during
// render) so TextBox never has to branch on numeric-ness itself, and so this logic reads and reuses
// the same way any other hook in the codebase does.
//
// Decimal types render their underlying <input> as type="text", not type="number": a native
// <input type="number"> silently sanitizes its value back to "" whenever it isn't a *complete*
// valid float (e.g. "10." has no digit after the decimal point, so it fails the HTML spec's
// floating-point grammar) — which fires on every keystroke for a controlled input, making it
// impossible to ever type past a trailing decimal point. Integer types never hit this (there's no
// "incomplete" integer state), so only decimal types need the workaround. All the same
// keydown/paste/blur validation still applies either way; it's just JS-enforced for decimals
// instead of leaning on the browser's native number parsing.
export function useNumericTextBoxBehavior({
    type, slotProps, onChange, onKeyDown, onPaste, onBlur,
}: UseNumericTextBoxBehaviorArgs): UseNumericTextBoxBehaviorResult {
    const numericType = isNumericType(type) ? type : undefined;
    const isDecimal = isDecimalType(numericType);
    const htmlType = numericType ? (isDecimal ? 'text' : 'number') : type;
    const appliesNumericGuards = htmlType === 'number' || isDecimal;

    return {
        htmlType,
        onKeyDown: appliesNumericGuards ? makeNumericKeyDownHandler(numericType, onKeyDown) : onKeyDown,
        onPaste: appliesNumericGuards ? makeNumericPasteHandler(numericType, onPaste) : onPaste,
        onBlur: appliesNumericGuards ? makeNumericBlurHandler(onChange, onBlur) : onBlur,
        slotProps: getAutoSlotProps(type, slotProps),
    };
}
